import React, { useCallback, useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { ButtonV2, LoadingLogo } from '@labrav/react-components'
import { useDispatch, useSelector } from '../../../store'
import { FooterV2 } from '../../../common/components/FooterV2/FooterV2'
import { isEmpty } from 'lodash'
import { useStyles } from './ButtonCreationFormikWrapper.styles'
import { ButtonInCreation } from '../../../modules/storefront/buttonCreation/reducer'
import ButtonCreationFormikValidationSchema from '../../../common/utils/ButtonCreationFormikValidationSchema'
import { ButtonsCanvas } from '../ButtonsCanvas/ButtonsCanvas'
import { ButtonName } from './ButtonName/ButtonName'
import { buttonData } from '../../../mocks/buttonCreationTouched.json'
import { ButtonLogoAvailability } from './LogoAvailabilityForm/ButtonLogoAvailabilityForm'
import { ButtonStyling } from './ButtonStyling/ButtonStyling'
import { ButtonSelect } from './ButtonSelect/ButtonSelect'

import {
  geButtonById,
  saveButtonStyleData,
} from '../../../modules/storefront/buttonCreation/action'
import { useNavigate, useParams } from 'react-router-dom'
import { useAccountId } from '@labrav/common'
import { EmbedCodeModal } from '../../FlyoutStorefrontV2/Storefront/EmbedCodeModal/EmbedCodeModal'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

export const ButtonCreationFormikWrapper: React.FC = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const buttonCreationFormData = useSelector<ButtonInCreation>(
    state => state.buttonCreation
  )
  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.BUTTON_STOREFRONT_LOADING)
  )
  const accountId = useAccountId({ product: 'flyout' })

  const { integrationId } = useParams<{
    integrationId: string
  }>()
  const currentIntegrationId =
    integrationId || buttonCreationFormData.buttonData.integrationId
  const integrationIdState = buttonCreationFormData.buttonData?.integrationId
  const currentTemplateId = buttonCreationFormData.buttonData.id
  useEffect(() => {
    if (!isEmpty(currentIntegrationId)) {
      window.history.pushState(
        {},
        '',
        `/flyout/storefront/builder/buttons/${currentIntegrationId}`
      )
    }
  }, [currentIntegrationId])

  useEffect(() => {
    const initializeData = async () => {
      if (!isEmpty(integrationId)) {
        await dispatch(geButtonById(integrationId || ''))
      }
    }
    initializeData()
  }, [integrationId])

  const noOps = () => void 0
  const [openlModal, setOpenModal] = useState(false)
  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleOpenModal = () => {
    setOpenModal(true)
  }
  const handleEmbedCode = useCallback(
    async (buttonData: ButtonInCreation) => {
      await dispatch(saveButtonStyleData(buttonData))
      await handleOpenModal()
    },
    [handleOpenModal, saveButtonStyleData]
  )

  useEffect(() => {
    const nameField = document.querySelector(
      '[name="buttonData.name"]'
    ) as HTMLInputElement | null
    if (nameField) {
      nameField.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])

  const sendButtonData = useCallback(
    async (buttonData: ButtonInCreation) => {
      await dispatch(saveButtonStyleData(buttonData))
    },
    [dispatch]
  )

  if (isLoading) {
    return <LoadingLogo />
  }
  return (
    <>
      <div
        className={classes.templateContainer}
        data-testid="storefront-template-form"
      >
        {!isEmpty(buttonCreationFormData) && (
          <Formik
            initialValues={buttonCreationFormData}
            validationSchema={ButtonCreationFormikValidationSchema}
            initialTouched={buttonData}
            validateOnMount={true}
            onSubmit={noOps}
            enableReinitialize={true}
          >
            {formikProps => {
              const { values, errors } = formikProps
              const handleSaveClick = () => {
                if (!values.buttonData.name) {
                  formikProps.setFieldTouched('buttonData.name', true, false)
                  const nameField = document.querySelector(
                    '[name="buttonData.name"]'
                  ) as HTMLInputElement | null
                  if (nameField) {
                    nameField.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    })
                    nameField.focus()
                  }
                  return
                }
                sendButtonData(formikProps.values)
              }
              return (
                <Form
                  className={classes.formStyle}
                  data-testid="formik-Container-button-creation"
                >
                  <div className={classes.templateForm}>
                    <div className={classes.root}>
                      <ButtonName />
                      <ButtonLogoAvailability />
                      <ButtonSelect showHorizontalDivider={false} />
                      <ButtonStyling />
                    </div>
                  </div>
                  <ButtonsCanvas />
                  <FooterV2
                    actions={
                      <div className={classes.footerWrapper}>
                        <div>{/* autosave */}</div>
                        <div className={classes.footerWrapperButtons}>
                          <ButtonV2
                            data-testid="save-button-template"
                            styleType="outlined"
                            onClick={handleSaveClick}
                            disabled={!isEmpty(errors)}
                          >
                            Save
                          </ButtonV2>
                          <ButtonV2
                            data-testid="embed-button-card"
                            styleType="primary"
                            onClick={() => handleEmbedCode(values)}
                            disabled={
                              isEmpty(integrationIdState) || !isEmpty(errors)
                            }
                          >
                            Get embed code snippet
                          </ButtonV2>
                        </div>
                      </div>
                    }
                    width={'calc(100vw - 27px)'}
                  />
                </Form>
              )
            }}
          </Formik>
        )}
      </div>

      <EmbedCodeModal
        integrationId={integrationIdState as string}
        partnerId={accountId as string}
        open={openlModal}
        onClose={handleCloseModal}
      />
    </>
  )
}
